import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// =====================================================================
// Sicherheits-Center: zeigt den aktuellen 2FA-Status, lässt den User
// wechseln / deaktivieren / Recovery-Codes neu generieren, und
// orchestriert die beiden Setup-Wizards (Push + TOTP).
// =====================================================================

const Setup2FA = () => {
  const { user, checkAuth } = useAuth();
  const navigate = useNavigate();
  // 'home' | 'wizard-push' | 'wizard-totp' | 'show-codes' | 'disable' | 'regenerate'
  const [view, setView] = useState('home');
  const [freshCodes, setFreshCodes] = useState([]);

  const goHomeAndRefresh = async () => {
    await checkAuth();
    setView('home');
  };

  const onCodesIssued = async (codes) => {
    if (codes && codes.length > 0) {
      setFreshCodes(codes);
      setView('show-codes');
    } else {
      await goHomeAndRefresh();
    }
    await checkAuth();
  };

  return (
    <div className="min-h-screen bg-background-subtle py-8 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl sm:text-3xl font-heading font-bold">Sicherheit</h1>
          <button
            onClick={() => navigate(user?.role === 'ADMIN' ? '/admin' : '/dashboard')}
            className="text-sm text-muted-foreground hover:text-primary"
            data-testid="back-to-dashboard"
          >
            ← Zurück
          </button>
        </div>

        {view === 'home' && (
          <SecurityHome
            user={user}
            onStartPush={() => setView('wizard-push')}
            onStartTotp={() => setView('wizard-totp')}
            onDisable={() => setView('disable')}
            onRegenerate={() => setView('regenerate')}
          />
        )}

        {view === 'wizard-push' && (
          <PushWizard
            onCancel={() => setView('home')}
            onSuccess={onCodesIssued}
          />
        )}

        {view === 'wizard-totp' && (
          <TotpWizard
            onCancel={() => setView('home')}
            onSuccess={onCodesIssued}
          />
        )}

        {view === 'show-codes' && (
          <RecoveryCodesView
            codes={freshCodes}
            onDone={goHomeAndRefresh}
          />
        )}

        {view === 'disable' && (
          <DisableFlow
            user={user}
            onCancel={() => setView('home')}
            onSuccess={goHomeAndRefresh}
          />
        )}

        {view === 'regenerate' && (
          <RegenerateFlow
            user={user}
            onCancel={() => setView('home')}
            onSuccess={(codes) => { setFreshCodes(codes); setView('show-codes'); }}
          />
        )}
      </div>
    </div>
  );
};

// =====================================================================
// 1) Übersicht (Home)
// =====================================================================
const SecurityHome = ({ user, onStartPush, onStartTotp, onDisable, onRegenerate }) => {
  const activeMethod =
    user?.twofa_push_enabled ? 'push' :
    user?.twofa_enabled ? 'totp' : null;

  return (
    <div className="space-y-6">
      <div className="card p-6">
        <h2 className="font-heading font-semibold text-lg mb-2">Zwei-Faktor-Authentifizierung (2FA)</h2>
        <p className="text-sm text-muted-foreground mb-4">
          Schütze deinen Account zusätzlich zum Passwort. Wir empfehlen die Aktivierung dringend.
        </p>

        {activeMethod === null && (
          <div className="text-sm rounded-lg bg-yellow-50 border border-yellow-200 px-4 py-3 mb-4" data-testid="twofa-status-inactive">
            <strong>Aktuell nicht aktiv.</strong> Wähle eine Methode unten zum Aktivieren.
          </div>
        )}
        {activeMethod === 'push' && (
          <div className="text-sm rounded-lg bg-green-50 border border-green-200 px-4 py-3 mb-4" data-testid="twofa-status-push">
            ✅ <strong>Push-Bestätigung (ValuePlus App)</strong> ist aktiv.
          </div>
        )}
        {activeMethod === 'totp' && (
          <div className="text-sm rounded-lg bg-green-50 border border-green-200 px-4 py-3 mb-4" data-testid="twofa-status-totp">
            ✅ <strong>Authenticator-App (Microsoft, Google, Authy)</strong> ist aktiv.
          </div>
        )}

        {activeMethod && (
          <RecoveryCodesStatus user={user} onRegenerate={onRegenerate} />
        )}
      </div>

      {/* Method cards */}
      <div className="grid sm:grid-cols-2 gap-4">
        <MethodCard
          title="Push-Bestätigung"
          subtitle="Mit der ValuePlus App"
          active={activeMethod === 'push'}
          color="gold"
          description="Web zeigt eine 2-stellige Zahl, die du in der ValuePlus App eintippst. Schnell und schick — keine externe App nötig."
          actionLabel={activeMethod === 'push' ? 'Bereits aktiv' : (activeMethod ? 'Auf Push wechseln' : 'Aktivieren')}
          onAction={onStartPush}
          disabled={activeMethod === 'push'}
          testId="method-push"
        />
        <MethodCard
          title="Authenticator-App"
          subtitle="Microsoft / Google / Authy"
          active={activeMethod === 'totp'}
          color="dark"
          description="Eine Authenticator-App auf deinem Handy generiert alle 30 Sekunden einen 6-stelligen Code, den du beim Login eintippst."
          actionLabel={activeMethod === 'totp' ? 'Bereits aktiv' : (activeMethod ? 'Auf TOTP wechseln' : 'Aktivieren')}
          onAction={onStartTotp}
          disabled={activeMethod === 'totp'}
          testId="method-totp"
        />
      </div>

      {activeMethod && (
        <div className="card p-6">
          <h3 className="font-semibold mb-3">Erweiterte Optionen</h3>
          <div className="space-y-3">
            <ActionRow
              label="Wiederherstellungscodes neu generieren"
              hint="Macht alle bisherigen Codes ungültig und gibt 12 neue Codes aus."
              buttonLabel="Neu generieren"
              onClick={onRegenerate}
              testId="action-regenerate"
            />
            <ActionRow
              label="2FA komplett deaktivieren"
              hint="Wir empfehlen das nicht — dein Account ist dann nur durch das Passwort geschützt."
              buttonLabel="Deaktivieren"
              onClick={onDisable}
              danger
              testId="action-disable"
            />
          </div>
        </div>
      )}
    </div>
  );
};

const MethodCard = ({ title, subtitle, active, description, actionLabel, onAction, disabled, color, testId }) => {
  const iconBg = color === 'gold'
    ? 'linear-gradient(145deg, #C5A059, #B0904F)'
    : 'linear-gradient(145deg, #1f1f1f, #0A0A0A)';
  return (
    <div className={`card p-6 flex flex-col ${active ? 'ring-2 ring-green-500' : ''}`} data-testid={testId}>
      <div className="flex items-start gap-4 mb-3">
        <div className="flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center text-white" style={{ background: iconBg }}>
          {color === 'gold' ? (
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
            </svg>
          ) : (
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          )}
        </div>
        <div>
          <h4 className="font-bold text-primary">{title}</h4>
          <p className="text-xs text-muted-foreground">{subtitle}</p>
        </div>
      </div>
      <p className="text-sm text-muted-foreground flex-1 mb-4">{description}</p>
      <button
        onClick={onAction}
        disabled={disabled}
        className={disabled ? 'btn-secondary opacity-60 cursor-not-allowed' : 'btn-primary'}
        data-testid={`${testId}-action`}
      >
        {actionLabel}
      </button>
    </div>
  );
};

const ActionRow = ({ label, hint, buttonLabel, onClick, danger, testId }) => (
  <div className="flex items-center justify-between gap-4">
    <div>
      <p className="font-medium text-sm">{label}</p>
      <p className="text-xs text-muted-foreground">{hint}</p>
    </div>
    <button
      onClick={onClick}
      className={danger
        ? 'text-sm px-4 py-2 rounded-lg border border-red-300 text-red-700 hover:bg-red-50 transition'
        : 'text-sm px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-50 transition'}
      data-testid={testId}
    >
      {buttonLabel}
    </button>
  </div>
);

const RecoveryCodesStatus = ({ user, onRegenerate }) => {
  const remaining = user?.recovery_codes_remaining ?? 0;
  const isLow = remaining > 0 && remaining <= 3;
  const isCritical = remaining === 0;

  if (isCritical) {
    return (
      <div className="text-sm rounded-lg bg-red-50 border border-red-300 px-4 py-3 flex items-start gap-3" data-testid="recovery-status-critical">
        <span className="text-lg">⚠</span>
        <div className="flex-1">
          <strong>Keine Wiederherstellungscodes mehr übrig!</strong>
          <p className="text-xs mt-1 text-red-900">
            Wenn du dein Handy verlierst, kommst du nicht mehr in deinen Account. Generiere jetzt neue Codes.
          </p>
        </div>
        <button
          onClick={onRegenerate}
          className="text-xs px-3 py-1.5 rounded-md bg-red-600 text-white hover:bg-red-700 transition whitespace-nowrap"
          data-testid="recovery-status-action-critical"
        >
          Jetzt generieren
        </button>
      </div>
    );
  }
  if (isLow) {
    return (
      <div className="text-sm rounded-lg bg-amber-50 border border-amber-300 px-4 py-3 flex items-start gap-3" data-testid="recovery-status-low">
        <span className="text-lg">⚠</span>
        <div className="flex-1">
          <strong>Nur noch {remaining} Wiederherstellungscode{remaining === 1 ? '' : 's'} übrig</strong>
          <p className="text-xs mt-1 text-amber-900">
            Generiere bald neue Codes, falls du dein Handy verlierst.
          </p>
        </div>
        <button
          onClick={onRegenerate}
          className="text-xs px-3 py-1.5 rounded-md bg-amber-600 text-white hover:bg-amber-700 transition whitespace-nowrap"
          data-testid="recovery-status-action-low"
        >
          Neu generieren
        </button>
      </div>
    );
  }
  return (
    <div className="text-xs text-muted-foreground flex items-center gap-2" data-testid="recovery-status-ok">
      <span>🔑 {remaining} von 12 Wiederherstellungscodes übrig</span>
    </div>
  );
};

const PlaceholderActionRow = ({ label, hint, buttonLabel, onClick, danger, testId }) => null;
// Kept as no-op to avoid touching downstream imports.

// =====================================================================
// 2) Wizard für PUSH (ValuePlus App)
// =====================================================================
const PushWizard = ({ onCancel, onSuccess }) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [confirmInstall, setConfirmInstall] = useState(false);
  const [confirmLogin, setConfirmLogin] = useState(false);

  const activate = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/2fa/push/enable`);
      toast.success('Push-Bestätigung aktiviert');
      onSuccess(res.data.recovery_codes || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Aktivierung fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="card p-6 sm:p-8 space-y-6" data-testid="push-wizard">
      <WizardHeader
        title="Push-Bestätigung aktivieren"
        step={step}
        total={3}
        onCancel={onCancel}
      />

      {step === 1 && (
        <div className="space-y-4">
          <h3 className="font-semibold">Schritt 1: ValuePlus App installieren</h3>
          <p className="text-sm text-muted-foreground">
            Du brauchst die ValuePlus App auf deinem Smartphone. Falls du sie schon hast, springe direkt weiter.
          </p>
          <div className="grid grid-cols-2 gap-3">
            <StoreButton
              label="App Store" sub="iPhone / iPad"
              href="https://apps.apple.com/"
              testId="push-wizard-appstore"
            />
            <StoreButton
              label="Google Play" sub="Android"
              href="https://play.google.com/store"
              testId="push-wizard-playstore"
            />
          </div>
          <label className="flex items-center gap-2 text-sm pt-2">
            <input
              type="checkbox"
              checked={confirmInstall}
              onChange={(e) => setConfirmInstall(e.target.checked)}
              data-testid="push-wizard-confirm-install"
            />
            <span>Die App ist auf meinem Handy installiert</span>
          </label>
          <div className="flex gap-3 pt-2">
            <button onClick={onCancel} className="btn-secondary flex-1">Abbrechen</button>
            <button
              onClick={() => setStep(2)}
              disabled={!confirmInstall}
              className="btn-primary flex-1"
              data-testid="push-wizard-next-1"
            >
              Weiter
            </button>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-4">
          <h3 className="font-semibold">Schritt 2: In der App anmelden</h3>
          <p className="text-sm text-muted-foreground">
            Öffne die ValuePlus App und melde dich mit derselben E-Mail-Adresse und demselben Passwort an, mit denen du dich gerade hier im Web angemeldet hast.
          </p>
          <div className="rounded-lg bg-amber-50 border border-amber-200 p-4 text-sm">
            <strong className="block mb-1">Warum?</strong>
            Die App muss wissen, dass sie deine Anmelde-Anfragen annehmen darf. Sobald du dort eingeloggt bist, kann jede Push-Anfrage durch Eintippen einer 2-stelligen Zahl bestätigt werden.
          </div>
          <label className="flex items-center gap-2 text-sm pt-2">
            <input
              type="checkbox"
              checked={confirmLogin}
              onChange={(e) => setConfirmLogin(e.target.checked)}
              data-testid="push-wizard-confirm-login"
            />
            <span>Ich bin in der ValuePlus App angemeldet</span>
          </label>
          <div className="flex gap-3 pt-2">
            <button onClick={() => setStep(1)} className="btn-secondary flex-1">Zurück</button>
            <button
              onClick={() => setStep(3)}
              disabled={!confirmLogin}
              className="btn-primary flex-1"
              data-testid="push-wizard-next-2"
            >
              Weiter
            </button>
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="space-y-4">
          <h3 className="font-semibold">Schritt 3: Aktivieren</h3>
          <p className="text-sm text-muted-foreground">
            Klicke unten auf <strong>„Jetzt aktivieren"</strong>. Ab der nächsten Anmeldung wird dir bei jedem Login eine 2-stellige Zahl angezeigt, die du in der ValuePlus App eintippst.
          </p>
          <div className="rounded-lg bg-blue-50 border border-blue-200 p-4 text-sm">
            <strong className="block mb-1">Tipp:</strong>
            Im Anschluss zeigen wir dir <strong>12 Wiederherstellungscodes</strong>. Bewahre sie sicher auf — du brauchst sie, falls du dein Handy verlierst.
          </div>
          <div className="flex gap-3 pt-2">
            <button onClick={() => setStep(2)} className="btn-secondary flex-1">Zurück</button>
            <button
              onClick={activate}
              disabled={loading}
              className="btn-primary flex-1"
              data-testid="push-wizard-activate"
            >
              {loading ? 'Wird aktiviert...' : 'Jetzt aktivieren'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// =====================================================================
// 3) Wizard für TOTP (Authenticator App)
// =====================================================================
const TotpWizard = ({ onCancel, onSuccess }) => {
  const [step, setStep] = useState(1);
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState('');
  const [secret, setSecret] = useState('');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);

  const startSetup = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/setup-2fa`);
      setQrCodeDataUrl(res.data.qr_code_data_url);
      setSecret(res.data.secret);
      setStep(3);
    } catch {
      toast.error('Setup fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  const verify = async () => {
    if (code.length !== 6) { toast.error('6-stelligen Code eingeben'); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/verify-2fa`, { code });
      toast.success('TOTP aktiviert');
      onSuccess(res.data.recovery_codes || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ungültiger Code');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="card p-6 sm:p-8 space-y-6" data-testid="totp-wizard">
      <WizardHeader
        title="Authenticator-App aktivieren"
        step={step}
        total={4}
        onCancel={onCancel}
      />

      {step === 1 && (
        <div className="space-y-4">
          <h3 className="font-semibold">Schritt 1: Authenticator-App installieren</h3>
          <p className="text-sm text-muted-foreground">
            Lade eine dieser kostenlosen Apps auf dein Smartphone. Falls du schon eine hast, springe direkt weiter.
          </p>
          <div className="space-y-2">
            <AuthAppRow
              name="Microsoft Authenticator"
              ios="https://apps.apple.com/de/app/microsoft-authenticator/id983156458"
              android="https://play.google.com/store/apps/details?id=com.azure.authenticator"
              testId="totp-wizard-ms"
            />
            <AuthAppRow
              name="Google Authenticator"
              ios="https://apps.apple.com/de/app/google-authenticator/id388497605"
              android="https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2"
              testId="totp-wizard-google"
            />
            <AuthAppRow
              name="Authy"
              ios="https://apps.apple.com/de/app/twilio-authy/id494168017"
              android="https://play.google.com/store/apps/details?id=com.authy.authy"
              testId="totp-wizard-authy"
            />
          </div>
          <div className="flex gap-3 pt-2">
            <button onClick={onCancel} className="btn-secondary flex-1">Abbrechen</button>
            <button onClick={() => setStep(2)} className="btn-primary flex-1" data-testid="totp-wizard-next-1">
              Weiter
            </button>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-4">
          <h3 className="font-semibold">Schritt 2: Setup starten</h3>
          <p className="text-sm text-muted-foreground">
            Klicke unten auf „QR-Code generieren". Auf der nächsten Seite öffnest du deine Authenticator-App, tippst auf das <strong>„+"-Symbol</strong> (oder „Konto hinzufügen") und wählst <strong>„QR-Code scannen"</strong>.
          </p>
          <div className="flex gap-3 pt-2">
            <button onClick={() => setStep(1)} className="btn-secondary flex-1">Zurück</button>
            <button onClick={startSetup} disabled={loading} className="btn-primary flex-1" data-testid="totp-wizard-generate">
              {loading ? 'Wird generiert...' : 'QR-Code generieren'}
            </button>
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="space-y-4">
          <h3 className="font-semibold">Schritt 3: QR-Code scannen</h3>
          <p className="text-sm text-muted-foreground">
            Öffne deine Authenticator-App, tippe auf <strong>„+"</strong> → <strong>„QR-Code scannen"</strong> und richte die Handy-Kamera auf den Code unten.
          </p>
          <div className="flex justify-center">
            <div className="bg-white p-4 rounded-lg border-2 border-border">
              <img src={qrCodeDataUrl} alt="2FA QR Code" className="w-48 h-48 sm:w-56 sm:h-56" data-testid="totp-wizard-qr" />
            </div>
          </div>
          <details className="text-sm">
            <summary className="cursor-pointer text-muted-foreground">QR-Code funktioniert nicht? Manuell eingeben</summary>
            <div className="mt-2 bg-background-subtle p-3 rounded">
              <p className="text-xs mb-2">Tippe in deiner App auf „Konto manuell hinzufügen" und gib diesen Schlüssel ein:</p>
              <code className="block text-xs bg-white px-3 py-2 rounded border break-all" data-testid="totp-wizard-secret">{secret}</code>
            </div>
          </details>
          <div className="flex gap-3 pt-2">
            <button onClick={() => setStep(2)} className="btn-secondary flex-1">Zurück</button>
            <button onClick={() => setStep(4)} className="btn-primary flex-1" data-testid="totp-wizard-next-3">
              Ich habe gescannt
            </button>
          </div>
        </div>
      )}

      {step === 4 && (
        <div className="space-y-4">
          <h3 className="font-semibold">Schritt 4: Code bestätigen</h3>
          <p className="text-sm text-muted-foreground">
            Deine App zeigt jetzt einen <strong>6-stelligen Code</strong>, der alle 30 Sekunden wechselt. Gib den aktuellen Code unten ein:
          </p>
          <input
            type="text"
            maxLength={6}
            className="input-field text-center text-2xl tracking-widest font-mono"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
            placeholder="000000"
            data-testid="totp-wizard-code-input"
            style={{ letterSpacing: '0.5em' }}
          />
          <div className="rounded-lg bg-blue-50 border border-blue-200 p-4 text-sm">
            <strong className="block mb-1">Im Anschluss:</strong>
            Wir zeigen dir <strong>12 Wiederherstellungscodes</strong>. Bewahre sie sicher auf, falls du dein Handy verlierst.
          </div>
          <div className="flex gap-3 pt-2">
            <button onClick={() => setStep(3)} className="btn-secondary flex-1">Zurück</button>
            <button
              onClick={verify}
              disabled={loading || code.length !== 6}
              className="btn-primary flex-1"
              data-testid="totp-wizard-verify"
            >
              {loading ? 'Wird geprüft...' : 'Aktivieren'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// =====================================================================
// 4) Recovery-Codes anzeigen
// =====================================================================
const RecoveryCodesView = ({ codes, onDone }) => {
  const download = () => {
    const blob = new Blob([codes.join('\n')], { type: 'text/plain' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'valueplus-recovery-codes.txt';
    document.body.appendChild(a);
    a.click();
    a.remove();
  };
  return (
    <div className="card p-6 sm:p-8" data-testid="recovery-codes-view">
      <div className="text-center mb-6">
        <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-3">
          <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h2 className="text-xl font-bold">2FA aktiviert</h2>
        <p className="text-sm text-muted-foreground mt-1">Speichere diese 12 Codes JETZT — sie werden nur einmal angezeigt.</p>
      </div>
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4 text-sm">
        ⚠ Jeder Code funktioniert nur EINMAL als Ersatz für den 2FA-Code, falls du keinen Zugriff auf dein Handy hast.
      </div>
      <div className="grid grid-cols-2 gap-2 font-mono text-sm mb-4">
        {codes.map((c, i) => (
          <div key={i} className="bg-background-subtle px-3 py-2 rounded text-center" data-testid={`recovery-code-${i}`}>{c}</div>
        ))}
      </div>
      <div className="flex gap-3">
        <button onClick={download} className="btn-secondary flex-1" data-testid="recovery-codes-download">
          📥 Herunterladen
        </button>
        <button onClick={onDone} className="btn-primary flex-1" data-testid="recovery-codes-done">
          Ich habe die Codes gespeichert
        </button>
      </div>
    </div>
  );
};

// =====================================================================
// 5) Disable Flow
// =====================================================================
const DisableFlow = ({ user, onCancel, onSuccess }) => {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/auth/disable-2fa`, { code });
      toast.success('2FA deaktiviert');
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ungültiger Code');
    } finally {
      setLoading(false);
    }
  };
  const hint = user?.twofa_enabled
    ? 'Gib den aktuellen 6-stelligen Code aus deiner Authenticator-App ein — oder einen ungenutzten Wiederherstellungscode.'
    : 'Gib einen ungenutzten Wiederherstellungscode ein (Format XXXX-XXXX). Push-Bestätigung kann nicht über die App selbst deaktiviert werden.';
  return (
    <div className="card p-6 sm:p-8 space-y-4" data-testid="disable-flow">
      <h3 className="font-semibold text-lg">2FA deaktivieren</h3>
      <p className="text-sm text-muted-foreground">{hint}</p>
      <input
        type="text"
        className="input-field text-center font-mono"
        placeholder={user?.twofa_enabled ? '000000' : 'XXXX-XXXX'}
        value={code}
        onChange={(e) => setCode(e.target.value)}
        maxLength={user?.twofa_enabled ? 6 : 9}
        data-testid="disable-flow-code-input"
      />
      <div className="flex gap-3">
        <button onClick={onCancel} className="btn-secondary flex-1">Abbrechen</button>
        <button onClick={submit} disabled={loading || !code} className="btn-primary flex-1" data-testid="disable-flow-submit">
          {loading ? 'Wird deaktiviert...' : '2FA deaktivieren'}
        </button>
      </div>
    </div>
  );
};

// =====================================================================
// 6) Regenerate Recovery Codes
// =====================================================================
const RegenerateFlow = ({ user, onCancel, onSuccess }) => {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/regenerate-recovery-codes`, { code });
      toast.success('12 neue Codes generiert');
      onSuccess(res.data.recovery_codes || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ungültiger Code');
    } finally {
      setLoading(false);
    }
  };
  const hint = user?.twofa_enabled
    ? 'Gib den aktuellen 6-stelligen Code aus deiner Authenticator-App ein — oder einen ungenutzten Wiederherstellungscode.'
    : 'Gib einen ungenutzten Wiederherstellungscode ein.';
  return (
    <div className="card p-6 sm:p-8 space-y-4" data-testid="regenerate-flow">
      <h3 className="font-semibold text-lg">Wiederherstellungscodes neu generieren</h3>
      <p className="text-sm text-muted-foreground">{hint}</p>
      <p className="text-xs text-amber-700">⚠ Alle bisherigen Codes werden ungültig.</p>
      <input
        type="text"
        className="input-field text-center font-mono"
        placeholder={user?.twofa_enabled ? '000000' : 'XXXX-XXXX'}
        value={code}
        onChange={(e) => setCode(e.target.value)}
        maxLength={user?.twofa_enabled ? 6 : 9}
        data-testid="regenerate-flow-code-input"
      />
      <div className="flex gap-3">
        <button onClick={onCancel} className="btn-secondary flex-1">Abbrechen</button>
        <button onClick={submit} disabled={loading || !code} className="btn-primary flex-1" data-testid="regenerate-flow-submit">
          {loading ? 'Wird generiert...' : 'Neue Codes generieren'}
        </button>
      </div>
    </div>
  );
};

// =====================================================================
// Shared components
// =====================================================================
const WizardHeader = ({ title, step, total, onCancel }) => (
  <div>
    <div className="flex items-center justify-between mb-2">
      <h2 className="text-xl font-heading font-bold">{title}</h2>
      <button onClick={onCancel} className="text-sm text-muted-foreground hover:text-primary" data-testid="wizard-cancel">
        ✕
      </button>
    </div>
    <div className="flex gap-1.5">
      {Array.from({ length: total }).map((_, i) => (
        <div
          key={i}
          className="h-1 flex-1 rounded-full transition-all"
          style={{ backgroundColor: i < step ? '#C5A059' : '#E4E4E7' }}
        />
      ))}
    </div>
    <p className="text-xs text-muted-foreground mt-1">Schritt {step} von {total}</p>
  </div>
);

const StoreButton = ({ label, sub, href, testId }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className="flex items-center gap-3 px-4 py-3 rounded-lg border border-gray-200 hover:border-[#C5A059] hover:bg-amber-50 transition"
    data-testid={testId}
  >
    <svg className="w-6 h-6 text-primary" fill="currentColor" viewBox="0 0 24 24">
      <path d="M7 4v16l13-8z" />
    </svg>
    <div>
      <div className="font-medium text-sm">{label}</div>
      <div className="text-xs text-muted-foreground">{sub}</div>
    </div>
  </a>
);

const AuthAppRow = ({ name, ios, android, testId }) => (
  <div className="flex items-center justify-between px-4 py-3 rounded-lg border border-gray-200" data-testid={testId}>
    <span className="text-sm font-medium">{name}</span>
    <div className="flex gap-2 text-xs">
      <a href={ios} target="_blank" rel="noopener noreferrer" className="text-[#C5A059] hover:underline">iOS</a>
      <span className="text-gray-300">|</span>
      <a href={android} target="_blank" rel="noopener noreferrer" className="text-[#C5A059] hover:underline">Android</a>
    </div>
  </div>
);

export default Setup2FA;
